// The shared well. Everyone's piece falls in the same well at the same time,
// and falling pieces are solid to each other.
//
// The rules that make it co-op (Matt, 2026-10-01):
//  - "if I try to move through your piece I bump your piece in my direction.
//    So we're all trying to avoid each other." A sideways move shoves any
//    falling piece in the way one cell along with it, through a chain of
//    pieces if need be. If anything in the chain can't move (wall, stack),
//    nothing moves.
//  - "down movements don't push. Just side to side." Gravity, soft drop and
//    hard drop all stop on another falling piece. A piece resting on a
//    falling piece never locks there; it waits until the piece under it has
//    locked (or moved out of the way), so nobody is ever stuck in the air
//    because of somebody else.
//  - Rotating into another piece is simply blocked (no shove). A rotation
//    shove would make pieces jump in ways nobody can follow.
//
// The state is a plain object, mutated in place by applyInput() and step().
// It carries no functions and no references into the UI, so it can be
// JSON-copied into a bug report as-is.

import { ROTATIONS, SHAPE_NAMES, kicksFor, pieceCells } from './pieces.js';
import { nextRandom } from './rng.js';

export const VISIBLE_ROWS = 20;
// Two rows of headroom above the visible well, where pieces spawn.
export const HIDDEN_ROWS = 2;
export const HEIGHT = VISIBLE_ROWS + HIDDEN_ROWS;

// Longer than the classic half second: with four people shoving each
// other in one well, a piece needs a moment longer to be nudged into place
// (a player's request, 2026-10-12).
export const LOCK_DELAY_MS = 800;
export const MAX_LOCK_RESETS = 15;
export const SOFT_DROP_MS = 40;
export const QUEUE_LENGTH = 3;
// A new piece starts fully in view, just under the rim: it comes in
// somewhere different every time, so it must be seen the moment it lands.
const SPAWN_Y = HIDDEN_ROWS;
// How long a cleared row flashes before it disappears is the renderer's
// business; the engine clears instantly and reports an event.

// A solo well is the classic ten wide. With company, five columns a head plus
// two of slack: 12 for two, 17 for three, 22 for four. Narrower per person
// than solo, on purpose - the crowding is the game.
export function wellWidth(playerCount) {
  return playerCount <= 1 ? 10 : playerCount * 5 + 2;
}

// Lines per level. Level 1 falls at a cell a second; the curve is the usual
// one, so level 10 is quick and level 15 is very quick.
export const LINES_PER_LEVEL = 10;
export function gravityMs(level) {
  return Math.max(16, 1000 * ((0.8 - (level - 1) * 0.007) ** (level - 1)));
}

const LINE_POINTS = [0, 100, 300, 500, 800];

// Bigger groups clear far fewer lines - the crowding and shoving make it
// hard - so their line points get a bonus. Matt, 2026-10-19: "I want to
// reward people for playing in larger groups." Saved games (2026-10) had
// three and four players clearing about 40% of the lines two did.
// 1x alone or in a pair, 2x for three, 2.5x for four, then half more for
// each extra person.
export function groupBonus(playerCount) {
  if (playerCount <= 2) return 1;
  if (playerCount === 3) return 2;
  return 2.5 + (playerCount - 4) * 0.5;
}
// Saved games carry this; games saved before the bonus have none, and
// Records multiplies their score by the bonus so everything ranks together.
export const SCORING_VERSION = 2;

function shuffledBag(state) {
  const bag = [...SHAPE_NAMES];
  for (let i = bag.length - 1; i > 0; i -= 1) {
    const j = Math.floor(nextRandom(state) * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return bag;
}

function drawShape(state, player) {
  if (player.bag.length === 0) player.bag = shuffledBag(state);
  return player.bag.shift();
}

// spawn: 'anywhere' (the couch game: a random spot across the whole well)
// or 'near' (party mode: near where your last piece landed - each phone
// only shows a slice of a very wide well, and a piece appearing 80 columns
// away would yank the view across the board every time).
export const NEAR_SPAN = 6;

export function createGame({ players = 2, seed = Date.now(), spawn = 'anywhere' } = {}) {
  const width = wellWidth(players);
  const state = {
    spawn,
    width,
    height: HEIGHT,
    grid: Array.from({ length: HEIGHT }, () => Array(width).fill(null)),
    players: [],
    rng: seed >>> 0,
    seed,
    score: 0,
    lines: 0,
    level: 1,
    time: 0,
    over: false,
    paused: false,
    // Things that just happened, for the renderer and sound to pick up.
    // Drained by whoever reads them (takeEvents).
    events: [],
  };
  for (let i = 0; i < players; i += 1) {
    const player = {
      id: i,
      piece: null,
      bag: [],
      queue: [],
      lockMs: 0,
      lockResets: 0,
      gravityMs: 0,
      softDrop: false,
      hardDropped: false,
      pieces: 0,
      lines: 0,
      // Blocks of this player's that were in a line when it cleared - the
      // end-of-game number. Matt: "how many squares each person contributed
      // to completed lines, not just who filled in the last squares."
      cleared: 0,
      // Where this player's pieces come in when spawn is 'near': the middle
      // of their share of the well to begin with, then wherever the last
      // one locked.
      home: Math.round((width / players) * (i + 0.5)),
    };
    for (let q = 0; q < QUEUE_LENGTH; q += 1) player.queue.push(drawShape(state, player));
    state.players.push(player);
  }
  state.players.forEach((p) => trySpawn(state, p));
  return state;
}

// ---- collision ----------------------------------------------------------

function hitsWell(state, cells) {
  return cells.some(([x, y]) => (
    x < 0 || x >= state.width || y >= state.height || (y >= 0 && state.grid[y][x])
  ));
}

function cellKey(x, y) { return `${x},${y}`; }

function occupiedBy(state, exceptIds) {
  const map = new Map();
  state.players.forEach((p) => {
    if (!p.piece || exceptIds.includes(p.id)) return;
    pieceCells(p.piece).forEach(([x, y]) => map.set(cellKey(x, y), p.id));
  });
  return map;
}

function hitsPieces(state, cells, selfId) {
  const others = occupiedBy(state, [selfId]);
  return cells.some(([x, y]) => others.has(cellKey(x, y)));
}

function moved(piece, dx, dy) {
  return { ...piece, x: piece.x + dx, y: piece.y + dy };
}

// Resting on the stack or the floor (as opposed to on someone's piece).
function onStack(state, player) {
  return hitsWell(state, pieceCells(moved(player.piece, 0, 1)));
}

function canFall(state, player) {
  const below = pieceCells(moved(player.piece, 0, 1));
  return !hitsWell(state, below) && !hitsPieces(state, below, player.id);
}

// ---- spawning, locking, clearing ---------------------------------------

// Every new piece comes in at a random spot along the top - anywhere it
// fits. Matt, 2026-10-01: with fixed entry points "the best strategy ...
// is going to be for everyone just to stay in their lane ... and that's
// boring. We need to do something in order to force people to cross paths."
//
// Spots where the stack is in the way are never chosen; the game is over
// only when the stack blocks the top everywhere. Spots taken by someone's
// falling piece are skipped too - and if those are the only spots left,
// the piece waits a tick for the way to clear.
function trySpawn(state, player) {
  const shape = player.queue[0];
  const cellsAt = (x) => pieceCells({ shape, rot: 0, x, y: SPAWN_Y });
  const offsets = ROTATIONS[shape][0].map(([x]) => x);
  const minX = 0 - Math.min(...offsets); // not -Math.min(): that makes -0
  const maxX = state.width - 1 - Math.max(...offsets);
  const clearOfStack = [];
  for (let x = minX; x <= maxX; x += 1) if (!hitsWell(state, cellsAt(x))) clearOfStack.push(x);
  if (clearOfStack.length === 0) {
    // The stack has reached the top all the way across: the end, for everyone.
    state.over = true;
    state.events.push({ type: 'over', playerId: player.id });
    return false;
  }
  let open = clearOfStack.filter((x) => !hitsPieces(state, cellsAt(x), player.id));
  if (state.spawn === 'near') {
    // Within NEAR_SPAN of home if there's room there; otherwise the
    // nearest open spot, so a buried neighbourhood moves you along
    // instead of ending the game.
    const centre = (x) => x + (Math.min(...offsets) + Math.max(...offsets) + 1) / 2;
    const near = open.filter((x) => Math.abs(centre(x) - player.home) <= NEAR_SPAN);
    if (near.length) open = near;
    else if (open.length) {
      const best = Math.min(...open.map((x) => Math.abs(centre(x) - player.home)));
      open = open.filter((x) => Math.abs(centre(x) - player.home) === best);
    }
  }
  if (open.length === 0) return false;
  const x = open[Math.floor(nextRandom(state) * open.length)];
  player.queue.shift();
  player.queue.push(drawShape(state, player));
  player.piece = { shape, rot: 0, x, y: SPAWN_Y };
  player.lockMs = 0;
  player.lockResets = 0;
  player.gravityMs = 0;
  player.hardDropped = false;
  state.events.push({ type: 'spawn', playerId: player.id, cells: pieceCells(player.piece) });
  return true;
}

function lock(state, player) {
  const cells = pieceCells(player.piece);
  const shape = player.piece.shape;
  player.piece = null;
  player.pieces += 1;
  if (cells.some(([, y]) => y < 0)) {
    state.over = true;
    state.events.push({ type: 'over', playerId: player.id });
    return;
  }
  cells.forEach(([x, y]) => { state.grid[y][x] = { owner: player.id, shape }; });
  player.home = cells.reduce((sum, [x]) => sum + x, 0) / cells.length + 0.5;
  state.events.push({ type: 'lock', playerId: player.id, cells });
  clearLines(state, player);
}

function clearLines(state, player) {
  const full = [];
  state.grid.forEach((row, y) => { if (row.every(Boolean)) full.push(y); });
  if (full.length === 0) return;

  // Whose blocks were in the cleared rows. A clear built by three people
  // scores three times - the shared well should pay for working together.
  const owners = new Set();
  full.forEach((y) => state.grid[y].forEach((cell) => {
    owners.add(cell.owner);
    const owner = state.players[cell.owner];
    if (owner) owner.cleared += 1;
  }));

  const fullSet = new Set(full);
  const kept = state.grid.filter((_, y) => !fullSet.has(y));
  const fresh = full.map(() => Array(state.width).fill(null));
  state.grid = [...fresh, ...kept];

  const points = LINE_POINTS[Math.min(full.length, 4)] * state.level * owners.size
    * groupBonus(state.players.length);
  state.score += points;
  state.lines += full.length;
  player.lines += full.length;
  const level = 1 + Math.floor(state.lines / LINES_PER_LEVEL);
  if (level > state.level) state.events.push({ type: 'level', level });
  state.level = level;
  state.events.push({
    type: 'clear', rows: full, count: full.length, owners: [...owners], points, playerId: player.id,
  });

  // The stack above the cleared rows just dropped, so falling pieces drop
  // with it: as many rows as were cleared below them. A piece tucked under
  // an overhang keeps its place instead of being buried and popped out on
  // top (bug report, 2026-10-05). Lowest first, a row at a time, stopping
  // on anything in the way, so pieces never end up inside each other.
  state.players
    .filter((p) => p.piece)
    .sort((a, b) => bottomRow(b.piece) - bottomRow(a.piece))
    .forEach((p) => {
      const below = full.filter((y) => y > bottomRow(p.piece)).length;
      for (let i = 0; i < below; i += 1) {
        const cells = pieceCells(moved(p.piece, 0, 1));
        if (hitsWell(state, cells) || hitsPieces(state, cells, p.id)) break;
        p.piece = moved(p.piece, 0, 1);
      }
    });

  // Backstop: if a piece still overlaps the stack somehow, lift it clear.
  state.players.forEach((p) => {
    if (!p.piece) return;
    let guard = 0;
    while (hitsWell(state, pieceCells(p.piece)) && guard < 4) {
      p.piece = moved(p.piece, 0, -1);
      guard += 1;
    }
  });
}

// A move or turn while grounded buys more time before locking - but only so
// many times, or a piece could be spun forever.
function touched(state, player) {
  if (onStack(state, player) && player.lockResets < MAX_LOCK_RESETS) {
    player.lockMs = 0;
    player.lockResets += 1;
  }
}

// ---- moves --------------------------------------------------------------

// Sideways, with the shove. Returns the ids that moved (mover first), or
// null if the move was blocked.
export function shift(state, playerId, dx) {
  const mover = state.players[playerId];
  if (!mover.piece) return null;
  const group = [playerId];
  for (let i = 0; i < group.length; i += 1) {
    const shiftedCells = pieceCells(moved(state.players[group[i]].piece, dx, 0));
    const others = occupiedBy(state, group);
    shiftedCells.forEach(([x, y]) => {
      const hit = others.get(cellKey(x, y));
      if (hit !== undefined && !group.includes(hit)) group.push(hit);
    });
  }
  const blocked = group.some((id) => hitsWell(state, pieceCells(moved(state.players[id].piece, dx, 0))));
  if (blocked) return null;
  group.forEach((id) => {
    const p = state.players[id];
    p.piece = moved(p.piece, dx, 0);
    touched(state, p);
  });
  if (group.length > 1) {
    state.events.push({ type: 'push', playerId, pushed: group.slice(1), dx });
  } else {
    state.events.push({ type: 'move', playerId, dx });
  }
  return group;
}

export function rotate(state, playerId, dir) {
  const player = state.players[playerId];
  if (!player.piece) return false;
  const from = player.piece.rot;
  const to = (from + (dir > 0 ? 1 : 3)) % 4;
  const kicks = kicksFor(player.piece.shape, from, to);
  for (const [kx, ky] of kicks) {
    const candidate = { ...player.piece, rot: to, x: player.piece.x + kx, y: player.piece.y + ky };
    const cells = pieceCells(candidate);
    if (!hitsWell(state, cells) && !hitsPieces(state, cells, playerId)) {
      player.piece = candidate;
      touched(state, player);
      state.events.push({ type: 'rotate', playerId });
      return true;
    }
  }
  return false;
}

export function hardDrop(state, playerId) {
  const player = state.players[playerId];
  if (!player.piece) return 0;
  let dropped = 0;
  while (canFall(state, player)) {
    player.piece = moved(player.piece, 0, 1);
    dropped += 1;
  }
  state.score += dropped * 2;
  state.events.push({ type: 'drop', playerId, rows: dropped });
  if (onStack(state, player)) {
    lock(state, player);
  } else {
    // Landed on somebody's falling piece. It stays slammed: the moment the
    // piece under it is gone, it falls the rest of the way and locks.
    player.hardDropped = true;
  }
  return dropped;
}

// One row down, if nothing's in the way - never a shove, and never a lock
// (a piece already resting stays put and its lock clock carries on).
export function stepDown(state, playerId) {
  const player = state.players[playerId];
  if (!player.piece || !canFall(state, player)) return false;
  player.piece = moved(player.piece, 0, 1);
  player.lockMs = 0;
  state.score += 1;
  return true;
}

export function applyInput(state, playerId, action) {
  if (state.over || state.paused) return;
  const player = state.players[playerId];
  if (!player) return;
  switch (action) {
    case 'left': shift(state, playerId, -1); break;
    case 'right': shift(state, playerId, 1); break;
    case 'rotateCW': rotate(state, playerId, 1); break;
    case 'rotateCCW': rotate(state, playerId, -1); break;
    case 'hardDrop': hardDrop(state, playerId); break;
    case 'stepDown': stepDown(state, playerId); break;
    case 'softOn': player.softDrop = true; break;
    case 'softOff': player.softDrop = false; break;
    default: break;
  }
}

// ---- time ---------------------------------------------------------------

function bottomRow(piece) {
  return Math.max(...pieceCells(piece).map(([, y]) => y));
}

export function step(state, dtMs) {
  if (state.over || state.paused) return;
  state.time += dtMs;

  state.players.forEach((p) => { if (!p.piece) trySpawn(state, p); });
  if (state.over) return;

  // Lowest piece first, so a stack of falling pieces falls together instead
  // of the top one waiting a tick for the one beneath it.
  const order = state.players
    .filter((p) => p.piece)
    .sort((a, b) => bottomRow(b.piece) - bottomRow(a.piece));

  for (const player of order) {
    if (!player.piece || state.over) continue;
    const interval = player.softDrop ? Math.min(SOFT_DROP_MS, gravityMs(state.level)) : gravityMs(state.level);

    if (canFall(state, player)) {
      if (player.hardDropped) {
        // Its support moved away: finish the slam now.
        hardDrop(state, player.id);
        continue;
      }
      player.gravityMs += dtMs;
      while (player.gravityMs >= interval && canFall(state, player)) {
        player.gravityMs -= interval;
        player.piece = moved(player.piece, 0, 1);
        player.lockMs = 0;
        if (player.softDrop) state.score += 1;
      }
      if (!canFall(state, player)) player.gravityMs = 0;
    } else if (onStack(state, player)) {
      if (player.hardDropped) { lock(state, player); continue; }
      player.lockMs += dtMs;
      if (player.lockMs >= LOCK_DELAY_MS) lock(state, player);
    } else {
      // Resting on someone else's falling piece: no lock clock. Gravity
      // keeps ticking but is capped at one row, so when the piece below
      // drops, this one follows it on the same tick instead of hanging a
      // whole interval behind - and never dumps several rows at once.
      player.gravityMs = Math.min(player.gravityMs + dtMs, interval);
      player.lockMs = 0;
    }
  }
}

export function takeEvents(state) {
  const events = state.events;
  state.events = [];
  return events;
}

// Where a piece would land if hard-dropped now (for the drop shadow).
export function ghostOf(state, playerId) {
  const player = state.players[playerId];
  if (!player.piece) return null;
  let ghost = player.piece;
  const fits = (p) => {
    const cells = pieceCells(p);
    return !hitsWell(state, cells) && !hitsPieces(state, cells, playerId);
  };
  while (fits(moved(ghost, 0, 1))) ghost = moved(ghost, 0, 1);
  return ghost;
}

export { pieceCells, ROTATIONS };
